import { useMultiStepFormContext } from "#src/hooks/useMultiStepForm";
import {
  CodeBlock,
  EmptyState,
  Icon,
  Panel,
  useToast,
} from "@validereinc/common-components";
import type { WorkerVersionCredentials } from "@validereinc/domain";
import { copyTextToClipboard } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React from "react";
import styles from "./CreateWorkerVersionFormPanels.module.scss";

const cx = classNames.bind(styles);

const WorkerVersionDeploymentInstructionsFormStep = ({
  stepNumber,
  credentials,
  versionNumber,
  isLoading,
}: {
  stepNumber: number;
  credentials?: WorkerVersionCredentials;
  versionNumber: number;
  isLoading: boolean;
}) => {
  const { currentStep } = useMultiStepFormContext();
  const { toast } = useToast();

  const handleCopy = (command: string) => {
    copyTextToClipboard(command).then(() =>
      toast.push({
        intent: "success",
        description: "Successfully copied command to your clipboard.",
      })
    );
  };

  const {
    access_key_id,
    secret_access_key,
    session_token,
    docker_repository_uri,
  } = credentials ?? {};

  const region = docker_repository_uri?.split(".")[3];
  const loginCommand = `AWS_ACCESS_KEY_ID="${access_key_id}" AWS_SECRET_ACCESS_KEY="${secret_access_key}" AWS_SESSION_TOKEN="${session_token}" aws ecr get-login-password --region ${region} | docker login --username AWS --password-stdin ${docker_repository_uri}`;
  const tagCommand = `docker tag my-image:local ${docker_repository_uri}:${versionNumber}`;
  const pushCommand = `docker push ${docker_repository_uri}:${versionNumber}`;

  const sectionConfigurations = [
    {
      sectionDescription: <>Set credentials and log in to the repository:</>,
      command: loginCommand,
    },
    {
      sectionDescription: (
        <>
          Replace <code>my-image:local</code> with the name of your image and
          tag it:
        </>
      ),
      command: tagCommand,
    },
    {
      sectionDescription: <>Push the image to the repository:</>,
      command: pushCommand,
    },
  ];

  const isMissingCredentials = !credentials;

  if (currentStep !== stepNumber) return null;

  return (
    <Panel
      className={cx("panel")}
      loaded={!isLoading}
    >
      <h4 className={cx("section-title")}>
        Worker Image Deployment Instructions
      </h4>
      {isMissingCredentials ? (
        <EmptyState
          title="No credentials found"
          suggestion="Please contact support"
          variant="warning"
          icon={<Icon variant="lock-simple" />}
        />
      ) : (
        <>
          <p className={cx("section-description")}>
            Ensure you have the AWS CLI and Docker installed, as well as your
            Docker image ready.
          </p>
          <p className={cx("section-description")}>
            Run the following commands to deploy your worker image to the
            repository. The credentials are only valid for 15 minutes.
          </p>
          {sectionConfigurations.map((section) => (
            <div key={section.command}>
              <p className={cx("section-description")}>
                {section.sectionDescription}
              </p>
              <CodeBlock
                onCopy={() => handleCopy(section.command)}
                isDisabled={isMissingCredentials}
                isLoading={isLoading}
              >
                {section.command}
              </CodeBlock>
            </div>
          ))}
        </>
      )}
    </Panel>
  );
};

export default WorkerVersionDeploymentInstructionsFormStep;
